import React from 'react';
import { useTranslation } from 'react-i18next';
import SEO, { createWebPageSchema, createBreadcrumbSchema } from '../components/SEO.tsx';

interface SocialLink {
  id: string;
  name: string;
  nameKo: string;
  url: string;
  icon: React.ReactNode;
  color: string;
  hoverColor: string;
}

const CONTACT_EMAIL = 'contact@bearlycodinglab.com';

const socialLinks: SocialLink[] = [
  {
    id: 'instagram',
    name: 'Instagram',
    nameKo: '인스타그램',
    url: 'https://www.instagram.com/bearlycodinglab.kor/',
    icon: (
      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
    ),
    color: 'bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400',
    hoverColor: 'hover:from-purple-700 hover:via-pink-600 hover:to-orange-500'
  },
  {
    id: 'youtube',
    name: 'YouTube',
    nameKo: '유튜브',
    url: 'https://www.youtube.com/@곰곰이코딩랩',
    icon: (
      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
    color: 'bg-red-500',
    hoverColor: 'hover:bg-red-600'
  },
  {
    id: 'email',
    name: CONTACT_EMAIL,
    nameKo: CONTACT_EMAIL,
    url: CONTACT_EMAIL,
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    color: 'bg-green-600',
    hoverColor: 'hover:bg-green-700'
  }
];

const Contact: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isKorean = i18n.language === 'ko';
  const [copied, setCopied] = React.useState(false);

  const handleEmailClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigator.clipboard.writeText(CONTACT_EMAIL);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const structuredData = [
    createWebPageSchema({
      title: `${t('pageTitle.contact')} | ${t('companyName')}`,
      description: isKorean
        ? '곰곰이 코딩랩의 다양한 채널에서 만나보세요.'
        : 'Connect with Bearly Coding Lab on various platforms.',
      url: '/contact'
    }),
    createBreadcrumbSchema([
      { name: isKorean ? '홈' : 'Home', url: '/' },
      { name: t('pageTitle.contact'), url: '/contact' }
    ])
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white pt-16">
      <SEO
        title={t('pageTitle.contact')}
        description={isKorean
          ? '곰곰이 코딩랩의 다양한 채널에서 만나보세요.'
          : 'Connect with Bearly Coding Lab on various platforms.'}
        keywords={['contact', 'social media', 'youtube', 'instagram', '연락', '소셜미디어']}
        canonicalUrl="/contact"
        structuredData={structuredData}
      />

      <div className="max-w-lg mx-auto px-4 sm:px-6 py-12">
        {/* Profile Section */}
        <div className="text-center mb-10">
          {/* Profile Image */}
          <div className="w-28 h-28 mx-auto mb-4 rounded-full bg-gradient-to-br from-green-400 to-green-600 p-1">
            <div className="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden">
              <img
                src="/images/gomgomi.webp"
                alt="Bearly Coding Lab"
                className="w-24 h-24 object-contain"
              />
            </div>
          </div>

          {/* Name */}
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            {isKorean ? '곰곰이 코딩랩' : 'Bearly Coding Lab'}
          </h1>

          {/* Registered English trade name */}
          {isKorean && (
            <p className="text-gray-500 text-sm font-medium mb-2">Bearly Coding Lab</p>
          )}

          {/* Bio */}
          <p className="text-gray-600 text-sm max-w-xs mx-auto">
            {isKorean
              ? '코딩을 쉽고 재미있게! 다양한 채널에서 만나요 👋'
              : 'Making coding fun & easy! Connect with us 👋'}
          </p>
        </div>

        {/* Social Links */}
        <div className="space-y-3">
          {socialLinks.map((link) => (
            link.id === 'email' ? (
              <button
                key={link.id}
                onClick={handleEmailClick}
                className={`flex items-center justify-center gap-3 w-full py-4 px-6 rounded-2xl text-white font-medium transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg ${link.color} ${link.hoverColor}`}
              >
                {link.icon}
                <span className="text-sm sm:text-base">
                  {copied ? (isKorean ? '복사됨!' : 'Copied!') : (isKorean ? link.nameKo : link.name)}
                </span>
              </button>
            ) : (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center justify-center gap-3 w-full py-4 px-6 rounded-2xl text-white font-medium transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg ${link.color} ${link.hoverColor}`}
              >
                {link.icon}
                <span>{isKorean ? link.nameKo : link.name}</span>
              </a>
            )
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-gray-400 text-sm">
            {isKorean ? '곰곰이 코딩랩 (Bearly Coding Lab)' : 'Bearly Coding Lab'} © {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Contact;
